import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, Eye, Brain, Calendar, User, CheckCircle2, Settings, Plus, Download, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { generarPDFIpv } from '../../utils/pdfGeneratorIpv';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import Card from '../../components/ui/Card';
import ipvService from '../../services/ipvService';
import perfilService from '../../services/perfilService';
import perfilPruebaService from '../../services/perfilPruebaService';
import useAuthStore from '../../store/authStore';

export default function ExamenesIpv() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [perfiles, setPerfiles] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAsignacionModal, setShowAsignacionModal] = useState(false);
  const [creandoExamenMaestro, setCreandoExamenMaestro] = useState(false);
  const [ipvExamenes, setIpvExamenes] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar TODOS los exámenes IPV desde /api/ipv-examenes
      const examenesData = await ipvService.listarExamenes();
      console.log('📊 Exámenes IPV cargados:', examenesData);
      setIpvExamenes(examenesData || []);

      // Cargar perfiles
      const perfilesResponse = await perfilService.getAllPerfiles({ page: 0, size: 1000 });
      console.log('Respuesta de perfiles:', perfilesResponse);
      
      // Manejar diferentes formatos de respuesta
      let perfilesData = [];
      if (perfilesResponse && typeof perfilesResponse === 'object' && perfilesResponse.content) {
        // Respuesta paginada
        perfilesData = perfilesResponse.content;
      } else if (Array.isArray(perfilesResponse)) {
        // Respuesta como array directo
        perfilesData = perfilesResponse;
      } else {
        // Fallback
        perfilesData = [];
      }
      
      setPerfiles(perfilesData || []);

      // Cargar asociaciones perfil-pruebas
      const asociacionesData = await perfilPruebaService.getAll();
      setAsociaciones(asociacionesData || []);
    } catch (error) {
      console.error('Error cargando datos IPV:', error);
      toast.error('Error al cargar exámenes IPV');
      setIpvExamenes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCrearExamenMaestro = async () => {
    try {
      setCreandoExamenMaestro(true);
      toast.loading('Creando examen maestro IPV...', { id: 'crear-maestro-ipv' });
      
      // Crear el examen maestro con el ID del usuario actual
      const nuevoExamen = await ipvService.crearExamen(user.id);
      
      toast.success(`Examen maestro IPV creado con ID: ${nuevoExamen.id}`, { id: 'crear-maestro-ipv' });
      
      await loadData();
    } catch (error) {
      console.error('Error creando examen maestro IPV:', error);
      toast.error('Error al crear el examen maestro IPV', { id: 'crear-maestro-ipv' });
    } finally {
      setCreandoExamenMaestro(false);
    }
  };

  const handleTogglePerfil = async (perfilId, isAssigned) => {
    try {
      console.log('🔄 handleTogglePerfil llamado:', { perfilId, isAssigned });
      
      if (isAssigned) {
        // Encontrar y eliminar la asociación
        const asociacion = asociaciones.find(
          a => a.ipvExamenId != null && a.perfilId === perfilId
        );
        console.log('❌ Desasignando asociación:', asociacion);
        
        if (asociacion) {
          await perfilPruebaService.delete(asociacion.id);
          toast.success('Examen IPV desasignado del perfil');
        }
      } else {
        // Crear nueva asociación (usando ipvExamenId = 1)
        console.log('✅ Asignando IPV Examen ID=1 al perfil:', perfilId);
        const resultado = await perfilPruebaService.asociarIpvExamen(perfilId, 1);
        console.log('✅ Resultado de asociación:', resultado);
        toast.success('Examen IPV asignado al perfil');
      }
      loadData();
    } catch (error) {
      console.error('❌ Error al actualizar asociación:', error);
      console.error('❌ Error detallado:', error.response?.data);
      toast.error('Error al actualizar asignación');
    }
  };

  const getPerfilesAsignados = () => {
    return asociaciones
      .filter(a => a.ipvExamenId != null)
      .map(a => {
        const perfil = perfiles.find(p => p.id === a.perfilId);
        return perfil ? perfil.nombre : `Perfil #${a.perfilId}`;
      });
  };

  // Filtrar exámenes IPV (no evaluaciones)
  const filteredExamenes = ipvExamenes
    .filter(ex => ex.id !== 1) // Excluir el examen maestro
    .filter(ex =>
      ex.usuarioNombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ex.usuarioId?.toString().includes(searchTerm)
    );

  // Verificar si existe el examen maestro con ID 1
  const existeExamenMaestro = ipvExamenes.some(e => e.id === 1);

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-800">Exámenes IPV</h1>
              <p className="text-neutral-600 mt-1">Resultados de evaluaciones de personalidad para vendedores</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!existeExamenMaestro && (
            <Button
              onClick={handleCrearExamenMaestro}
              disabled={creandoExamenMaestro}
              className="gap-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
            >
              <Plus className="w-4 h-4" />
              {creandoExamenMaestro ? 'Creando...' : 'Crear Examen Maestro IPV'}
            </Button>
          )}
          {existeExamenMaestro && (
            <Button 
              onClick={() => setShowAsignacionModal(true)} 
              className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              <Settings className="w-4 h-4" />
              Asignar a Perfiles
            </Button>
          )}
        </div>
      </div>

      {/* Alerta si no existe el examen maestro */}
      {!existeExamenMaestro && (
        <Card className="border-l-4 border-l-red-500 bg-red-50">
          <div className="p-4">
            <div className="flex items-start gap-3">
              <Brain className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-800 mb-1">⚠️ Examen Maestro IPV No Existe</h3>
                <p className="text-sm text-red-700 mb-3">
                  El sistema IPV requiere un examen maestro con <strong>ID = 1</strong> para funcionar correctamente.
                  Este examen se utiliza como referencia para asignar el Examen IPV a los perfiles.
                </p>
                <div className="bg-red-100 rounded-lg p-3 mt-2">
                  <p className="text-xs text-red-800">
                    <strong>📋 Acción requerida:</strong> Haz clic en el botón "Crear Examen Maestro IPV" 
                    para generar el examen con ID 1. Una vez creado, podrás asignarlo a los perfiles deseados.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Card de perfiles asignados */}
      {existeExamenMaestro && (
        <Card className="border-l-4 border-l-purple-500">
          <div className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-neutral-800 mb-2">Perfiles con Examen IPV Habilitado</h3>
                {getPerfilesAsignados().length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {getPerfilesAsignados().map((nombre, idx) => (
                      <Badge key={idx} variant="primary" className="bg-gradient-to-r from-white-600 to-indigo-600">
                        {nombre}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 italic">
                    No hay perfiles con Examen IPV habilitado. Haz clic en "Asignar a Perfiles" para configurar.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tarjeta Informativa del Test IPV */}
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <div className="flex flex-col md:flex-row">
          {/* Imagen */}
          <div className="md:w-1/3 h-48 md:h-auto bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center relative overflow-hidden">
            <img 
              src="/assets/ipv-test.jpg" 
              alt="Test IPV" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center" style={{display: 'none'}}>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-white/20 flex items-center justify-center mb-3 shadow-lg">
                  <Users className="w-12 h-12 text-white" />
                </div>
                <span className="text-white/90 font-semibold text-lg">IPV</span>
              </div>
            </div>
            <div className="absolute top-3 right-3">
              <Badge className="bg-white/20 text-white border-white/30">Comercial</Badge>
            </div>
          </div>
          
          {/* Contenido */}
          <div className="md:w-2/3 p-6">
            <h3 className="text-2xl font-bold text-neutral-800 mb-3 flex items-center gap-2">
              <Users className="w-6 h-6 text-purple-600" />
              Inventario de Personalidad para Vendedores (IPV)
            </h3>
            <p className="text-neutral-600 mb-4">
              Instrumento psicométrico diseñado específicamente para evaluar las aptitudes comerciales y la disposición para la venta.
              Mide tanto la disposición general como dimensiones específicas del perfil vendedor.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Escalas Principales</h4>
                <ul className="space-y-1 text-sm text-neutral-600">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                    <strong>DGV:</strong> Disposición General para la Venta
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                    <strong>R:</strong> Receptividad
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                    <strong>A:</strong> Agresividad comercial
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                    <strong>I-II:</strong> Comprensión y Adaptabilidad
                  </li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Características</h4>
                <ul className="space-y-1 text-sm text-neutral-600">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span>
                    87 ítems de evaluación
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span>
                    Duración: 35-45 minutos
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span>
                    9 dimensiones evaluadas
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-pink-500 rounded-full"></span>
                    Reporte PDF con percentiles
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 pt-4 border-t border-neutral-100">
              <div className="flex items-center gap-2 text-xs bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full">
                <span>💼</span> Selección de vendedores
              </div>
              <div className="flex items-center gap-2 text-xs bg-pink-50 text-pink-700 px-3 py-1.5 rounded-full">
                <span>📈</span> Potencial comercial
              </div>
              <div className="flex items-center gap-2 text-xs bg-fuchsia-50 text-fuchsia-700 px-3 py-1.5 rounded-full">
                <span>🎯</span> Alta validez predictiva
              </div>
            </div>
          </div>
        </div>
      </Card>

{/* Filtros y tabla de exámenes 

      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar por usuario, DNI..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-neutral-200">
            <tr>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Usuario</th>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Estado</th>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Fecha</th>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredExamenes.length === 0 ? (
              <tr>
                <td colSpan="4" className="p-8 text-center text-neutral-500">
                  <Brain className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
                  <p>No se encontraron exámenes IPV</p>
                </td>
              </tr>
            ) : (
              filteredExamenes.map((examen) => {
                const getEstadoBadge = (estado) => {
                  const estados = {
                    'PENDIENTE': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Pendiente' },
                    'PROGRESO': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'En Progreso' },
                    'FINALIZADO': { color: 'bg-green-100 text-green-800 border-green-200', text: 'Finalizado' }
                  };
                  const config = estados[estado] || { color: 'bg-gray-100 text-gray-800 border-gray-200', text: estado || 'N/A' };
                  return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                      {config.text}
                    </span>
                  );
                };

                return (
                  <tr key={examen.id} className="hover:bg-neutral-50">
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <span className="font-medium text-neutral-800">{examen.usuarioNombre || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">{getEstadoBadge(examen.estado)}</td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2 text-neutral-600">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">
                          {examen.fechaCreacion ? new Date(examen.fechaCreacion).toLocaleDateString('es-PE') : 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/examenes-ipv/resultados/${examen.ipvEvaluacionId}`)}
                          title="Ver resultados interpretados"
                          disabled={!examen.ipvEvaluacionId}
                        >
                          <Eye className="w-4 h-4" />
                          Ver Detalle
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={async () => {
                            try {
                              toast.loading('Cargando datos y generando PDF...', { id: 'pdf-ipv' });
                              // Obtener el resultado interpretado
                              const resultado = await ipvService.obtenerResultadoInterpretado(examen.ipvEvaluacionId);
                              // Agregar el nombre del usuario desde la tabla
                              resultado.usuarioNombre = examen.usuarioNombre;
                              resultado.nombrePostulante = examen.usuarioNombre;
                              // Generar el PDF con los datos
                              await generarPDFIpv(resultado);
                              toast.success('PDF generado exitosamente', { id: 'pdf-ipv' });
                            } catch (error) {
                              console.error('Error generando PDF:', error);
                              toast.error('Error al generar el PDF', { id: 'pdf-ipv' });
                            }
                          }}
                          title="Descargar PDF"
                          disabled={!examen.ipvEvaluacionId}
                        >
                          <Download className="w-4 h-4" />
                          PDF
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de asignación a perfiles */}
      <AsignacionPerfilesIpvModal
        isOpen={showAsignacionModal}
        onClose={() => setShowAsignacionModal(false)}
        perfiles={perfiles}
        asociaciones={asociaciones}
        onTogglePerfil={handleTogglePerfil}
      />
    </div>
  );
}

function AsignacionPerfilesIpvModal({ isOpen, onClose, perfiles, asociaciones, onTogglePerfil }) {
  const isPerfilAsignado = (perfilId) => {
    return asociaciones.some(
      a => a.ipvExamenId === 1 && a.perfilId === perfilId
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Asignar Examen IPV a Perfiles"
      size="lg"
    >
      <div className="space-y-4">
        <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Brain className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
            <div>
              <p className="text-sm text-purple-900 font-medium mb-2">
                <strong>Examen IPV de 87 Preguntas</strong>
              </p>
              <p className="text-xs text-purple-700">
                Selecciona los perfiles que podrán realizar el Examen IPV. Los usuarios asignados a estos perfiles 
                verán el examen disponible en su panel con las 87 preguntas del Inventario de Personalidad para Vendedores.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {perfiles.length === 0 ? (
            <p className="text-center text-neutral-500 py-8">No hay perfiles disponibles</p>
          ) : (
            perfiles.map((perfil) => {
              const asignado = isPerfilAsignado(perfil.id);
              return (
                <div
                  key={perfil.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    asignado
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-neutral-200 hover:border-purple-300 hover:bg-neutral-50'
                  }`}
                  onClick={() => onTogglePerfil(perfil.id, asignado)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-neutral-800">{perfil.nombre}</p>
                      {perfil.descripcion && (
                        <p className="text-sm text-neutral-600 mt-1">{perfil.descripcion}</p>
                      )}
                    </div>
                    <div className="ml-4">
                      {asignado ? (
                        <CheckCircle2 className="w-6 h-6 text-purple-600" />
                      ) : (
                        <div className="w-6 h-6 border-2 border-neutral-300 rounded-full"></div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
